import React, { useEffect, useState } from "react";
import { api, apiJson } from "@/api/client.js";
import { NewPointsBanner, SupperBanner } from "@/components/HuntMorning.jsx";
import { BIG_BREAKFAST, CONVOYS, FREEZER, LUNCH, MEET_POINT, OTHER_MORNINGS, ROAD_STOP } from "@/content/huntBoard.js";
import { HUNT_SUPPERS } from "@/content/huntMeals.js";
import { isLiveTask } from "@/components/CockpitPanels.jsx";
import {
  dayNumberFromIso, isoDate, localStampClock, stayState, supperIndexForDate, torontoClock,
} from "@/lib/huntDates.js";
import { weatherFace, windFr } from "@/lib/weatherFace.js";

const WEATHER_KEY = "orignal2026.weather";

function wholeNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? String(Math.round(number)) : "—";
}

function wholeDegree(value) {
  const number = wholeNumber(value);
  return number === "—" ? "—" : `${number}°`;
}

function compass(dir) {
  const named = windFr(dir);
  if (named && named !== dir) return named;
  const degrees = Number(dir);
  if (!Number.isFinite(degrees)) return "—";
  const labels = ["N", "NE", "E", "SE", "S", "SO", "O", "NO"];
  return labels[Math.round((((degrees % 360) + 360) % 360) / 45) % 8];
}

function readReturn(day) {
  try {
    return JSON.parse(window.localStorage.getItem(`orignal2026.return.${day}`) || "{}");
  } catch {
    return {};
  }
}

function downloadSummary(journal) {
  const plan = journal.plan || {};
  const weather = journal.weather || {};
  const lines = [
    `<h1>Journal du ${Number(journal.date.slice(8))} octobre 2026</h1>`,
    `<p>Météo enregistrée : ${weather.temperature ?? "—"} °C · ${weather.wind || "vent non noté"} · pluie ${weather.rain ?? "—"} %</p>`,
    `<p>Plan : ${plan.party || "pas décidé"} · ${plan.vehicle || "véhicule non noté"} · ${plan.spot || "zone non notée"}</p>`,
    `<p>NordQuest : version ${journal.nordquest?.version || "1"} · ${journal.nordquest?.new_count ?? 0} nouveaux</p>`,
    `<p>Observations : ${(journal.observations || []).map((row) => row.title).join(" · ") || "aucune"}</p>`,
    `<p>Tâches terminées : ${(journal.tasks_done || []).join(" · ") || "aucune"}</p>`,
    `<p>Dépenses : ${(journal.expenses || []).map((row) => `${row.description} ${row.amount}`).join(" · ") || "aucune"}</p>`,
    `<p>Résultat : ${journal.result || "—"}</p>`,
    `<p>Note : ${journal.note || "—"}</p>`,
  ];
  const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"><title>Journal ${journal.date}</title><style>body{font-family:Georgia,serif;background:#fffdf8;color:#14211b;padding:28px;line-height:1.45}h1{font-size:22px}</style></head><body>${lines.join("")}</body></html>`;
  const link = document.createElement("a");
  link.href = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  link.download = `journal-${journal.date}.html`;
  link.click();
}

export function HuntWeather({ day, onSnapshot }) {
  const [data, setData] = useState(null);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  useEffect(() => {
    let stop = false;
    api(`/api/weather?day=${day}`).then((body) => {
      if (stop) return;
      if (body?.ok) {
        window.localStorage.setItem(WEATHER_KEY, JSON.stringify(body));
        setData(body);
        return;
      }
      const cached = window.localStorage.getItem(WEATHER_KEY);
      setData(cached ? { ...JSON.parse(cached), stale: true, offline: !navigator.onLine } : { ok: false });
    }).catch(() => {
      if (stop) return;
      const cached = window.localStorage.getItem(WEATHER_KEY);
      setData(cached ? { ...JSON.parse(cached), stale: true, offline: true } : { ok: false, offline: !navigator.onLine });
    });
    return () => { stop = true; };
  }, [day]);
  useEffect(() => {
    if (data?.ok && onSnapshot) {
      const live = day === isoDate();
      const sample = (data.hourly || [])[2] || {};
      onSnapshot({
        temperature: live ? data.current?.temperature : data.daily?.max,
        wind: live
          ? `${data.current?.wind_dir || ""} ${data.current?.wind_speed || ""}`.trim()
          : `${sample.wind_dir || ""} ${sample.wind || ""}`.trim(),
        rain: data.daily?.rain,
        fetched_at: data.fetched_at || "",
      });
    }
  }, [data, onSnapshot]);
  if (!data) return <p className="og-hint" data-testid="weather-card">Météo…</p>;
  if (!data.ok) {
    return (
      <section data-testid="weather-card">
        <p>Météo indisponible pour le moment.</p>
        <p data-testid="weather-fresh">Dernière mise à jour météo : inconnue</p>
        <p>{online ? "En ligne" : "Hors ligne"}</p>
      </section>
    );
  }
  const hours = (data.hourly || []).filter((hour) => ["07", "09", "11", "13", "15", "17"].includes(String(hour.hour || "").slice(0, 2)));
  const fresh = data.fetched_at ? torontoClock(data.fetched_at) : "—";
  const moments = data.moments || {};
  const blocks = [
    ["morning", "Matin", false],
    ["noon", "Midi", false],
    ["afternoon", "Après-midi", false],
    ["night", "Nuit", true],
  ];
  return (
    <section data-testid="weather-card">
      <div className="og-sunline">
        <span>🌅 Lever {localStampClock(data.sunrise)}</span>
        <span className="og-sunline__bar">🌅 Matin — Midi — PM — 🌇</span>
        <span>🌇 Coucher {localStampClock(data.sunset)}</span>
      </div>
      <div className="og-moments" data-testid="weather-moments">
        {blocks.map(([key, label, night]) => {
          const row = moments[key] || {};
          const face = weatherFace(row.weather_code, night || row.night);
          return (
            <article key={key}>
              <h4>{label}</h4>
              <p>{face.icon} {wholeDegree(row.temperature)} — {face.label}</p>
              <p>Vent {compass(row.wind_dir)} {wholeNumber(row.wind)} km/h</p>
              <p>Pluie {wholeNumber(row.rain)} %</p>
            </article>
          );
        })}
      </div>
      <details className="og-hours-fold" data-testid="weather-hours-fold" open={typeof window.matchMedia === "function" && window.matchMedia("(min-width: 900px)").matches ? true : undefined}>
        <summary>Voir le détail heure par heure</summary>
        <table className="og-hours" data-testid="weather-hours">
          <thead>
            <tr>
              <th>Heure</th>
              <th>Température</th>
              <th>Vent</th>
              <th>Pluie</th>
            </tr>
          </thead>
          <tbody>
            {hours.map((hour) => (
              <tr key={hour.hour}>
                <td>{Number(String(hour.hour).slice(0, 2))} h</td>
                <td>{wholeDegree(hour.temperature)}</td>
                <td>{compass(hour.wind_dir)} {wholeNumber(hour.wind)} km/h</td>
                <td>{wholeNumber(hour.rain)} %</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
      <p data-testid="weather-fresh">Dernière mise à jour : {fresh}</p>
      {!online || data.offline || data.stale ? <p data-testid="weather-stale">Données possiblement périmées</p> : null}
      <p data-testid="weather-state">{!online || data.offline ? "Hors ligne — dernière météo connue" : "Secteur Desrochers"}</p>
    </section>
  );
}

function PlanSlot({ day, onEdit }) {
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    api(`/api/day-plan?day=${day}`).then((body) => {
      const next = body?.date ? body : { date: day, party: "", vehicle: "", spot: "", note: "" };
      if (body?.date) window.localStorage.setItem(`orignal2026.plan.${day}`, JSON.stringify(next));
      setPlan(next);
    }).catch(() => {
      try {
        const cached = window.localStorage.getItem(`orignal2026.plan.${day}`);
        setPlan(cached ? JSON.parse(cached) : { party: "", vehicle: "", spot: "", note: "" });
      } catch {
        setPlan({ party: "", vehicle: "", spot: "", note: "" });
      }
    });
  }, [day]);
  const empty = !plan || (!plan.party && !plan.vehicle && !plan.spot && !plan.note);
  return (
    <div data-testid="day-plan-slot">
      {empty ? <p>Pas encore décidé ce matin.</p> : (
        <ul className="og-dayfacts">
          {plan.party ? <li>Chasseurs · {plan.party}</li> : <li>Chasseurs · —</li>}
          {plan.vehicle ? <li>Véhicule · {plan.vehicle}</li> : null}
          {plan.spot ? <li>Zone / point · {plan.spot}</li> : null}
          {plan.note ? <li>{plan.note}</li> : null}
        </ul>
      )}
      <button type="button" className="og-tile" data-testid="morning-plan" onClick={onEdit}>Modifier le plan du jour</button>
    </div>
  );
}

function Meals({ day }) {
  const index = supperIndexForDate(day);
  const supper = index >= 0 ? HUNT_SUPPERS[index] : null;
  return (
    <div data-testid="meals-day">
      <h2>Repas du jour</h2>
      <p><strong>Déjeuner</strong> {OTHER_MORNINGS}</p>
      <p>{BIG_BREAKFAST}</p>
      <p><strong>Lunch</strong> {LUNCH}</p>
      {supper ? <p data-testid="supper-banner"><strong>Souper</strong> {supper.who} — {supper.dish}</p> : <p data-testid="supper-banner"><strong>Souper</strong> pas confirmé</p>}
      <p>{FREEZER}</p>
    </div>
  );
}

function DepartFacts({ open }) {
  return (
    <div className="og-dayfacts" data-testid="depart-day">
      <p>Départ vers 6 h</p>
      <p>{MEET_POINT}</p>
      {CONVOYS.map((convoy) => (
        <p key={convoy.id}>{convoy.title} — {convoy.lines[0]}</p>
      ))}
      <p>Pierre et Dan : convoi à confirmer.</p>
      <p>Marcel appelle Sépaq pendant le trajet · 418 323-2021</p>
      <p>{ROAD_STOP}</p>
      <p>Sépaq 13 h 45 · 229 rue du Lac-Vert</p>
      <p>Arrivée au chalet après l’enregistrement</p>
      <button type="button" className="og-tile" onClick={() => open("chalet")}>Installation</button>
      <p>Chasse possible à 16 h 30</p>
      <SupperBanner iso="2026-10-02" />
    </div>
  );
}

function LastDay() {
  return (
    <div data-testid="last-day">
      <p>Dernière chasse</p>
      <p>Fin de chasse 16 h</p>
      <p>Sortie du chalet 12 h</p>
      <p>Retour : à confirmer</p>
      <p>Dépenses finales à entrer</p>
    </div>
  );
}

export function CloseDay({ day, weather, onDone }) {
  const [journal, setJournal] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api(`/api/hunt-journal/${day}`).then(setJournal).catch(() => setJournal({ date: day, closed: false }));
  }, [day]);
  async function save(event) {
    event.preventDefault();
    const form = event.target;
    const stored = readReturn(day);
    try {
      const saved = await apiJson(`/api/hunt-journal/${day}/close`, {
        result: form.result.value,
        note: form.note.value,
        tomorrow: form.tomorrow.value || stored.tomorrow || "",
        weather,
        return_checks: stored,
      }, "POST");
      setJournal(saved);
      if (onDone) onDone(saved);
    } catch {
      setError("La journée n’est pas fermée. Réessayer quand le réseau revient.");
    }
  }
  const draft = journal?.draft || journal || {};
  return (
    <div data-testid="close-day">
      <p>Orignal2026 prépare le résumé avec ce qu’il connaît déjà. Complète seulement ce qui manque.</p>
      <ul className="og-dayfacts">
        <li>Météo : {weather?.temperature ?? "—"} °C · {weather?.wind || "vent non noté"}</li>
        <li>Plan : {draft.plan?.party || journal?.plan?.party || "pas encore décidé"}</li>
        <li>NordQuest : version {draft.nordquest?.version || journal?.nordquest?.version || "1"}</li>
        <li>Observations : {(draft.observations || journal?.observations || []).length}</li>
        <li>Tâches terminées : {(draft.tasks_done || journal?.tasks_done || []).length}</li>
        <li>Dépenses : {(draft.expenses || journal?.expenses || []).length}</li>
      </ul>
      {journal?.closed ? <p>Journée fermée.</p> : (
        <form className="og-task-compose" onSubmit={save}>
          <label>Résultat<input className="og-input" name="result" placeholder="Ce que l’application ne peut pas savoir" /></label>
          <label>Note libre<textarea className="og-input" name="note" rows={3} /></label>
          <label>Note pour demain<input className="og-input" name="tomorrow" defaultValue={readReturn(day).tomorrow || ""} /></label>
          <button type="submit" className="og-btn og-btn--primary">Enregistrer le journal</button>
        </form>
      )}
      {journal?.closed ? <button type="button" className="og-tile" data-testid="download-journal" onClick={() => downloadSummary(journal)}>Télécharger le résumé de la journée</button> : null}
      {error ? <p>{error}</p> : null}
    </div>
  );
}

function PastJournal({ day, admin, onClose }) {
  const [journal, setJournal] = useState(null);
  useEffect(() => {
    api(`/api/hunt-journal/${day}`).then((body) => {
      const next = body?.date ? body : { date: day, closed: false };
      if (body?.date) window.localStorage.setItem(`orignal2026.journal.${day}`, JSON.stringify(next));
      setJournal(next);
    }).catch(() => {
      try {
        const cached = window.localStorage.getItem(`orignal2026.journal.${day}`);
        setJournal(cached ? { ...JSON.parse(cached), offline: true } : { date: day, closed: false, offline: true });
      } catch {
        setJournal({ date: day, closed: false, offline: true });
      }
    });
  }, [day]);
  if (!journal) return <p className="og-hint">Journal…</p>;
  return (
    <article className="og-workcard" data-testid="day-journal">
      <h2>Résumé du {Number(day.slice(8))} octobre</h2>
      {journal.offline ? <p>Hors ligne — dernier journal connu</p> : null}
      {journal.closed ? (
        <>
          <p>Météo enregistrée : {journal.weather?.temperature ?? "—"} °C · {journal.weather?.wind || "vent non noté"}</p>
          <p>Plan : {journal.plan?.party || "pas noté"}</p>
          <p>Résultat : {journal.result || "—"}</p>
          <p>{journal.note}</p>
          <button type="button" className="og-tile" data-testid="download-journal" onClick={() => downloadSummary(journal)}>Télécharger le résumé de la journée</button>
        </>
      ) : (
        <p>Pas encore fermée. Le passé ne change pas tout seul.</p>
      )}
      {day === "2026-10-02" ? <DepartFacts open={() => {}} /> : null}
      {day === "2026-10-09" ? <LastDay /> : null}
      <Meals day={day} />
      {admin && !journal.closed ? <button type="button" className="og-tile" onClick={onClose}>Fermer la journée</button> : null}
    </article>
  );
}

export function HuntDay({ anchor, selected, onSelect, open, tasks, myHunter, admin, setWeather }) {
  const day = selected || anchor;
  const state = stayState(day, anchor);
  const mine = (tasks || []).filter((task) => isLiveTask(task) && task.assignee_id === myHunter?.id && task.status !== "done");
  const shown = mine.slice(0, 2);
  return (
    <div className="og-fit__board og-huntjournal" data-testid="cycle-board">
      {day === "2026-10-02" ? (
        <article className="og-workcard og-huntjournal__wide" data-testid="morning-board">
          <h2>2 octobre · départ</h2>
          <DepartFacts open={open} />
        </article>
      ) : null}

      {state === "future" && day !== "2026-10-02" ? (
        <article className="og-workcard og-huntjournal__wide" data-testid="day-future">
          <h2>{Number(day.slice(8))} octobre</h2>
          <p>Journée pas encore commencée.</p>
          <HuntWeather day={day} />
          <PlanSlot day={day} onEdit={() => open("plan")} />
          <SupperBanner iso={day} />
        </article>
      ) : null}

      {state === "past" && day !== "2026-10-02" ? (
        <PastJournal day={day} admin={admin} onClose={() => open("close-day")} />
      ) : null}

      {state === "today" && day !== "2026-10-02" ? (
        <>
          <article className="og-workcard" data-testid="morning-board">
            <h2>Météo · jour {dayNumberFromIso(day)}</h2>
            <HuntWeather day={day} onSnapshot={setWeather} />
          </article>
          <article className="og-workcard" data-testid="nordquest-block">
            <h2>NordQuest</h2>
            <NewPointsBanner onOpen={() => open("nordquest")} />
            <div className="og-fit__actions">
              <button type="button" className="og-tile" onClick={() => open("nordquest")}>Télécharger la mise à jour</button>
              <button type="button" className="og-tile" onClick={() => open("nordquest")}>J’ai installé cette version</button>
              <button type="button" className="og-tile" onClick={() => open("nordquest")}>Déposer mes points ce soir</button>
            </div>
          </article>
          <article className="og-workcard">
            <h2>Plan du jour</h2>
            <PlanSlot day={day} onEdit={() => open("plan")} />
          </article>
          <article className="og-workcard">
            <Meals day={day} />
          </article>
          {day === "2026-10-09" ? (
            <article className="og-workcard og-huntjournal__wide"><LastDay /></article>
          ) : null}
          <article className="og-workcard">
            <h2>Mes tâches — {mine.length} restante{mine.length > 1 ? "s" : ""}</h2>
            <ul className="og-dayfacts">
              {shown.map((task) => <li key={task.id}>{task.title}</li>)}
              {mine.length === 0 ? <li>Rien d’ouvert</li> : null}
            </ul>
            <button type="button" className="og-tile" onClick={() => open(myHunter?.id || "mine")}>Voir toutes mes tâches</button>
          </article>
          <section className="og-huntacts og-huntjournal__wide">
            <article data-testid="action-terrain">
              <h2>Terrain</h2>
              <button type="button" data-testid="open-territory" onClick={() => open("territory-read")}>Lire le territoire</button>
              <button type="button" data-testid="open-observation" onClick={() => open("observation")}>Ajouter une observation</button>
            </article>
            <article data-testid="action-return">
              <h2>Retour au chalet</h2>
              <button type="button" onClick={() => open("nordquest")}>Déposer mes points NordQuest</button>
              <button type="button" onClick={() => open("expenses")}>Ajouter une dépense</button>
              <button type="button" data-testid="open-return" onClick={() => open("return")}>Retour au chalet</button>
              {admin ? <button type="button" data-testid="close-day-open" onClick={() => open("close-day")}>Fermer la journée</button> : null}
            </article>
            <article data-testid="action-harvest">
              <h2>Si on récolte</h2>
              <button type="button" data-testid="open-harvest" onClick={() => open("harvest")}>Orignal abattu</button>
            </article>
          </section>
        </>
      ) : null}
    </div>
  );
}
